import { isPlausibleIpAddress } from './ipAddress';

/**
 * Classify ip_addresses rows as DNS A (IPv4) records.
 * Supports legacy ipType values IPv4 and missing type with IPv4-shaped address.
 */
export function isARecord(ipRow) {
  if (!ipRow) return false;
  const t = (ipRow.ipType || '').toUpperCase();
  if (t === 'A' || t === 'IPV4') return true;
  if (t === 'AAAA' || t === 'IPV6') return false;
  const addr = ipRow.ipAddress || '';
  return isPlausibleIpAddress(addr) && !addr.includes(':');
}

/**
 * Classify ip_addresses rows as DNS AAAA (IPv6) records.
 */
export function isAaaaRecord(ipRow) {
  if (!ipRow) return false;
  const t = (ipRow.ipType || '').toUpperCase();
  if (t === 'AAAA' || t === 'IPV6') return true;
  if (t === 'A' || t === 'IPV4') return false;
  const addr = ipRow.ipAddress || '';
  return addr.includes(':');
}

export function filterARecords(ipAddresses) {
  return (ipAddresses || []).filter(isARecord);
}

export function filterAaaaRecords(ipAddresses) {
  return (ipAddresses || []).filter(isAaaaRecord);
}

/** Classify a sibling IP as DNS A or AAAA using ip_addresses rows when available. */
export function ipRecordTypeForAddress(ipAddresses, targetIp) {
  if (!targetIp) return null;
  const row = (ipAddresses || []).find((ip) => ip.ipAddress === targetIp);
  if (row) {
    if (isAaaaRecord(row)) return 'AAAA';
    if (isARecord(row)) return 'A';
  }
  return targetIp.includes(':') ? 'AAAA' : 'A';
}

/** Dropdown options for Domains on Same IP (one entry per A / AAAA sibling address). */
export function buildDomainsOnIpFilterOptions(ipAddresses, domainsOnIp) {
  const options = [];
  const seen = new Set();

  const push = (ip, type) => {
    if (!ip || seen.has(ip)) return;
    seen.add(ip);
    options.push({ value: ip, label: `${type} — ${ip}` });
  };

  for (const row of filterARecords(ipAddresses)) {
    push(row.ipAddress, 'A');
  }
  for (const row of filterAaaaRecords(ipAddresses)) {
    push(row.ipAddress, 'AAAA');
  }
  for (const row of domainsOnIp || []) {
    push(row.targetIp, ipRecordTypeForAddress(ipAddresses, row.targetIp) || 'A');
  }

  return options;
}

export function filterDomainsOnIpBySiblingIp(domainsOnIp, selectedIp) {
  if (!selectedIp || selectedIp === 'ALL') return domainsOnIp || [];
  return (domainsOnIp || []).filter((row) => row.targetIp === selectedIp);
}

/** Dropdown options for Domains on Same NS (one entry per nameserver). */
export function buildDomainsOnNsFilterOptions(nameservers, domainsOnNs) {
  const options = [];
  const seen = new Set();

  const push = (host) => {
    const trimmed = host != null ? String(host).trim() : '';
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    options.push({ value: trimmed, label: trimmed });
  };

  for (const row of nameservers || []) {
    push(row.nameserver);
  }
  for (const row of domainsOnNs || []) {
    push(row.nameserver);
  }

  return options;
}

export function filterDomainsOnNsByNameserver(domainsOnNs, selectedNameserver) {
  if (!selectedNameserver || selectedNameserver === 'ALL') return domainsOnNs || [];
  return (domainsOnNs || []).filter((row) => row.nameserver === selectedNameserver);
}

/** SecurityTrails SOA primary nameserver (mname); legacy rows may store it in rname. */
export function soaPrimaryNameserver(soa) {
  if (!soa) return null;
  if (soa.mname) return soa.mname;
  const rname = soa.rname != null ? String(soa.rname) : '';
  if (rname && !rname.includes('@')) return rname;
  return null;
}

/** SOA admin mailbox when present (not a misfiled hostname). */
export function soaAdminMailbox(soa) {
  if (!soa?.rname) return null;
  const rname = String(soa.rname);
  return rname.includes('@') ? rname : null;
}

/** One-line SOA summary for compact SecurityTrails rows. */
export function soaDenseTitleLine(soa) {
  if (!soa) return '';
  const parts = [];
  const primary = soaPrimaryNameserver(soa);
  if (primary) parts.push(primary);
  const mailbox = soaAdminMailbox(soa);
  if (mailbox) parts.push(`RNAME ${mailbox}`);
  if (soa.minimum != null && soa.minimum !== '') parts.push(`TTL ${soa.minimum}`);
  return parts.length ? parts.join(' · ') : (soa.domain || 'SOA record');
}
